import { Component, HostListener, OnDestroy, OnInit, Renderer2 } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { BehaviorSubject, catchError, filter, forkJoin, Observable, of, Subject, take, takeUntil } from 'rxjs';
import { MatOptionSelectionChange } from '@angular/material/core';
import { Store } from '@ngrx/store';

import { ClientApi } from '@api/services/client-api';
import { SystemMessageService } from '@app/core/services/system-message.service';
import { TranslateConfigService } from '@app/core/translate-config.service';
import { AuthService } from '@app/core/auth/auth.service';
import { CustomEmailValidator } from '@app/core/services/media.service';
import { Customer, CustomerApi } from '@api/index';
import { selectUserCustomers } from '@app/store/user/user.selectors';
import { AdvertisingType } from '@api/models/advertising-type';
import { Client } from '@api/models/client';
import { indicateLoading } from '@app/core/indicate-loading';
import { EnumObject, EnumService } from '@app/core/services/enum.service';
import { CountriesApi } from '@api/services/countries-api.service';
import { UserRoleEnum } from '@app/admin/user/model/user.model';
import { CountriesResponse } from '@shared/models/country';
import { CURRENCIES } from '@shared/constants/currency.constants';
import { PRICING_MODEL } from '@shared/constants/pricing-model.constants';
import { CurrencyEnum } from '@shared/models/enums/currency.enum';
import { PricingModelEnum } from '@app/shared/models/enums/pricingModel.enum';

@Component({
  selector: 'flow-client-create',
  templateUrl: './client-create.component.html',
  styleUrls: ['./client-create.component.scss'],
})
export class ClientCreateComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  countries$: Observable<CountriesResponse[]>;
  adTypes: EnumObject<AdvertisingType>[];
  form: FormGroup;
  isAdmin: boolean;
  customers: Array<Customer>;
  clientId: number;
  public _reload = true;
  isEdit: boolean = false;
  loading$ = {
    init: new BehaviorSubject(true),
    save: new BehaviorSubject(false),
    upload: new BehaviorSubject(false),
  };
  currencies = CURRENCIES;
  pricingModels = PRICING_MODEL;
  customerId = null;
  activeStep: number = 0;
  steps = ['createClient.generalInformation', 'createClient.invoice'];
  CurrencyEnum = CurrencyEnum;
  
  constructor(
    public enumService: EnumService,
    private customerApi: CustomerApi,
    private renderer: Renderer2,
    private formBuilder: FormBuilder,
    private clientApi: ClientApi,
    private systemMessageService: SystemMessageService,
    private translateConfigService: TranslateConfigService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private authService: AuthService,
    private store: Store,
    private countriesService: CountriesApi

  ) { }

  @HostListener('document:keypress', ['$event'])
  handleKeyboardEvent(event: KeyboardEvent) {
    const elementExists = document.getElementsByClassName('country-search').length;
    if (elementExists) {
      const element = this.renderer?.selectRootElement('.country-search');
      const x = element as HTMLElement;
      setTimeout(() => x.focus(), 200);
    }
  }

  ngOnInit(): void {
    this.adTypes = this.enumService.advertisingType;
    this.initForm();
    this.clientId = +this.activatedRoute.snapshot.params['id'] || null;
    this.isEdit = Boolean(this.clientId);
    const user = this.authService.userData;
    this.isAdmin = user.roleId === UserRoleEnum.Admin && !user.currentCustomerId && !user.customerId;
    this.customerId = user?.currentCustomerId || user?.customerId;
    this.countries$ = this.getScandinavianCountries();

    forkJoin([
      this.isAdmin
        ? this.store.select(selectUserCustomers).pipe(
          filter(customers => customers && customers.length > 0),
          take(1)
        )
        : this.customerApi.getCustomerById(user.currentCustomerId || user.customerId),
      this.isEdit ? this.clientApi.getClientById(this.clientId) : of(null),
    ])
      .pipe(takeUntil(this.destroy$), indicateLoading(this.loading$.init))
      .subscribe(([customers, client]) => {
        this.customers = (this.isAdmin ? customers : [customers]) as Customer[];
        this.initForm(client);
        if (!this.isAdmin) this.form.get('customerId').setValue(this.customers[0].id);
      });
  }


  getScandinavianCountries(): Observable<CountriesResponse[]> {
    return this.countriesService.getScandinavianCountries().pipe(
      catchError(() => {
        this.systemMessageService.error(this.translateConfigService.instant('notifications.error.genericError'));
        return of([]);
      })
    );
  }

  private initForm(client?: Client) {
    this.form = this.formBuilder.group({
      customerId: [this.customerId || client?.customer?.id || '', Validators.required],
      name: [client?.name || '', Validators.required],
      vatNumber: [client?.vatNumber || ''],
      frameworkAgreementEnabled: [client?.frameworkAgreementEnabled || false],
      advertisingType: [client?.advertisingType || null],
      additionalInformation: [client?.additionalInformation || ''],
      address: [client?.address || ''],
      postalCode: [client?.postalCode || ''],
      city: [client?.city || ''],
      country: [client?.country || 'SE', Validators.required],
      emailAddress: [client?.emailAddress || '', [CustomEmailValidator(), Validators.required]],
      contactPerson: [client?.contactPerson || ''],
      phoneNumber: [client?.phoneNumber || ''],
      department: [client?.department || ''],
      costCenter: [client?.costCenter || ''],
      currency: [client?.currency || CurrencyEnum.SEK, Validators.required],
      pricingModel: [client?.pricingModel || PricingModelEnum.FP, Validators.required],
      paymentTerms: [client?.paymentTerms || ''],
      shouldCreateFolder: [client?.shouldCreateFolder || false],
    });
    if (client?.customer?.id) this.form.get('customerId').disable();
  }
  saveClient() {
    const callFunction = this.isEdit
      ? this.clientApi.editClient(this.form.getRawValue(), this.clientId)
      : this.clientApi.createClient(this.form.getRawValue());
    callFunction.pipe(takeUntil(this.destroy$), indicateLoading(this.loading$.init)).subscribe((client: Client) => {
      if (client) {
        this.systemMessageService.success(
          this.translateConfigService.instant(
            this.isEdit ? 'notifications.success.clientEdited' : 'notifications.success.clientCreate',
            this.form.getRawValue().name
          )
        );
        this.router.navigate(['/clients']);
      }
    });
  }

  setActiveStep(index: number, event: Event) {
    let element = event.currentTarget as HTMLElement;
    if (!element.classList.contains('disabled')) {
      this.activeStep = index;
    }
    this.activeStep = index;
  }

  onCancel() {
    this.router.navigate(['/clients']);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
